import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import { api } from "../lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";

const initial = {
  name: "",
  email: "",
  phone: "",
  delivery_mode: "online",
  motivation: "",
};

export default function EnrollPage() {
  const { programId } = useParams();
  const [program, setProgram] = useState(null);
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/programs").then((r) => {
      const p = (r.data.programs || []).find((x) => x.id === programId);
      setProgram(p);
    }).catch(() => {});
  }, [programId]);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error("Name and email are required.");
    setLoading(true);
    try {
      await api.post("/enroll", {
        ...form,
        program_id: programId,
        program_name: program ? program.name : programId,
      });
      toast.success("Enrollment received. We'll be in touch within 48 hours.");
      setForm(initial);
    } catch (err) {
      toast.error("Could not enroll. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="enroll-page">
      <Navbar />

      <section className="pt-36 pb-16 bg-radial-sky">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Link to="/#programs" className="inline-flex items-center gap-2 text-sm text-[#1D4E89] hover:text-[#0B2B40] mb-8" data-testid="back-to-programs">
            <ArrowLeft size={14} /> Back to programs
          </Link>
          <div className="text-xs tracking-[0.3em] uppercase text-[#1D4E89] font-medium mb-5">Enrollment</div>
          <h1 className="font-serif text-4xl md:text-5xl text-[#0B2B40] font-light tracking-tight leading-tight">
            {program ? program.name : "Program enrollment"}
          </h1>
          {program && (
            <p className="mt-5 text-lg text-[#4A5568] max-w-2xl font-light">{program.description}</p>
          )}
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-testid="enroll-form"
            onSubmit={submit}
            className="bg-[#F8F9FA] p-8 md:p-12 border border-slate-100"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-[#4A5568]">Full name *</label>
                <input data-testid="enroll-name-input" className="input-underline" value={form.name} onChange={handle("name")} />
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-[#4A5568]">Email *</label>
                <input data-testid="enroll-email-input" type="email" className="input-underline" value={form.email} onChange={handle("email")} />
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-[#4A5568]">Phone</label>
                <input data-testid="enroll-phone-input" className="input-underline" value={form.phone} onChange={handle("phone")} />
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-[#4A5568]">Delivery mode *</label>
                <select data-testid="enroll-mode-select" className="input-underline" value={form.delivery_mode} onChange={handle("delivery_mode")}>
                  <option value="online">Online</option>
                  <option value="in-person">In-person</option>
                </select>
              </div>
            </div>
            <div className="mt-6">
              <label className="text-xs tracking-[0.2em] uppercase text-[#4A5568]">What's drawing you to this work?</label>
              <textarea data-testid="enroll-motivation-input" className="input-underline" rows={4} value={form.motivation} onChange={handle("motivation")} />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="enroll-submit-button"
              className="mt-10 bg-[#0B2B40] text-white hover:bg-[#1D4E89] disabled:opacity-60 px-9 py-4 text-sm font-medium tracking-wide transition-colors duration-300"
            >
              {loading ? "Enrolling..." : "Submit Enrollment"}
            </button>
          </motion.form>
        </div>
      </section>

      <Footer />
    </div>
  );
}
